// AI generated

import type { Node } from '@xyflow/react'
import { useReactFlow } from '@xyflow/react'
import { useCallback } from 'react'
import { getNodePositionInsideParent, getNodeSize } from '../lib/bounds'
import useDetachNodes from './useDetachNodes'

/**
 * `onNodeDragStop`: a node dropped onto a group becomes its child, a child
 * dropped outside every group is detached from its parent.
 */
export function useNodeGrouping(setNodes: (nodes: Node[]) => void) {
  const { getIntersectingNodes, getNodes, getInternalNode } = useReactFlow()
  const detachNodes = useDetachNodes()

  return useCallback(
    (_: MouseEvent | TouchEvent, node: Node) => {
      if (node.type !== 'custom' && !node.parentId) {
        return
      }

      const groupNode = getIntersectingNodes(node).find(
        (n) => n.type === 'group',
      )

      if (!groupNode) {
        if (node.parentId) detachNodes([node.id])
        return
      }

      if (node.parentId === groupNode.id) return

      // node.position is parent-relative, the group's is absolute
      const position = getNodePositionInsideParent(
        {
          ...node,
          position:
            getInternalNode(node.id)?.internals.positionAbsolute ??
            node.position,
        },
        groupNode,
      )

      // a node too big for the group would hang out of it and slip past the
      // group's own minWidth/minHeight, so grow the group instead
      const nodeSize = getNodeSize(node)
      const groupSize = getNodeSize(groupNode)
      const grownSize = {
        width: Math.max(groupSize.width, position.x + nodeSize.width),
        height: Math.max(groupSize.height, position.y + nodeSize.height),
      }
      const groupMustGrow =
        grownSize.width > groupSize.width || grownSize.height > groupSize.height

      setNodes(
        getNodes().map((n) => {
          if (n.id === node.id) {
            return { ...n, position, parentId: groupNode.id }
          }

          if (groupMustGrow && n.id === groupNode.id) {
            return { ...n, ...grownSize, measured: grownSize }
          }

          return n
        }),
      )
    },
    [detachNodes, getIntersectingNodes, getInternalNode, getNodes, setNodes],
  )
}
